// Settings → Usage (S-2): what each consumer called, from the Hub's minute counts (GET /admin/v1/usage). Counts and
// times only: no body, no key, no personal data. Kept 90 days; the Hub writes them every 10 seconds.
import { useEffect, useState } from 'react'
import { Activity, Ban, Gauge, TriangleAlert } from 'lucide-react'
import { SegmentedControl, StatCard, formatDuration } from '@/kit/ui'
import { apiMode } from '@/lib/hubApi'
import { getUsage, scopeLabel, type ApiUsage, type UsagePeriod } from '@/lib/keysApi'

const PERIODS = [{ value: '1', label: 'Today' }, { value: '7', label: '7 days' }, { value: '30', label: '30 days' }] as const
const n = (v: number) => v.toLocaleString('en-IN')

export function UsageTab() {
  const [days, setDays] = useState<UsagePeriod>(1)
  const [u, setU] = useState<ApiUsage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = (d: UsagePeriod) => getUsage(d).then((r) => { setU(r); setError(null) }, (e: Error) => setError(e.message))
  useEffect(() => { if (apiMode()) void load(days) }, [days])

  if (!apiMode()) return <p className="text-xs text-muted">The calls of each consumer, per hour or day, against its limits. This needs the Hub API.</p>
  return (
    <div className="space-y-3" data-testid="usage">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedControl size="sm" ariaLabel="Usage of" value={String(days) as '1' | '7' | '30'} onChange={(v) => setDays(Number(v) as UsagePeriod)} options={[...PERIODS]} />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void load(days)}>Refresh</button>
      </div>
      {error && <p className="field-error">{error}</p>}
      {!u && !error && <p className="text-2xs text-muted">Loading…</p>}
      {u && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard icon={Activity} label="Calls" value={n(u.calls)} hint="with a consumer's key" />
            <StatCard icon={TriangleAlert} label="Errors" value={n(u.errors)} hint="answered 4xx or 5xx (not 429)" tone={u.errors ? 'danger' : 'gray'} />
            <StatCard icon={Ban} label="Refused by a limit" value={n(u.limited)} hint="429 Too many calls" tone={u.limited ? 'accent' : 'gray'} />
            <StatCard icon={Gauge} label="Time" value={u.avgMs === null ? '—' : formatDuration(u.avgMs)} hint={u.p95Ms === null ? 'no calls' : `95% within about ${formatDuration(u.p95Ms)}`} />
          </div>
          <section className="card">
            <h2 className="section-title">Calls per {u.step}</h2>
            <UsageChart u={u} />
          </section>
          <section className="card overflow-x-auto">
            <h2 className="section-title">Per consumer</h2>
            <table className="table" data-testid="usage-consumers">
              <thead><tr><th>Consumer</th><th>Scope</th><th>Calls</th><th>Errors</th><th>429</th><th>Time</th><th>Limit</th><th>Today / daily quota</th></tr></thead>
              <tbody>
                {u.perConsumer.flatMap((c) => (c.scopes.length ? c.scopes : [null]).map((s, i) => (
                  <tr key={`${c.name}-${s?.scope ?? 'none'}`}>
                    <td>{i === 0 && <>{c.title}{!c.active && <span className="text-2xs text-muted"> · off</span>}</>}</td>
                    {s ? (
                      <>
                        <td className="text-2xs">{scopeLabel(s.scope)}</td>
                        <td>{n(s.calls)}</td>
                        <td className={s.errors ? 'text-red-600 dark:text-red-400' : ''}>{n(s.errors)}</td>
                        <td className={s.limited ? 'text-amber-600 dark:text-amber-300' : ''}>{n(s.limited)}</td>
                        <td className="text-2xs">{s.avgMs === null ? '—' : formatDuration(s.avgMs)}{s.p95Ms !== null && <span className="block text-muted">95% {formatDuration(s.p95Ms)}</span>}</td>
                        <td className="text-2xs">{s.perMinute ? `${n(s.perMinute)} / min` : '—'}</td>
                        <td className="min-w-[140px]"><Quota used={s.usedToday} of={s.perDay} /></td>
                      </>
                    ) : <td colSpan={7} className="text-2xs text-muted">may call nothing</td>}
                  </tr>
                )))}
              </tbody>
            </table>
          </section>
          <section className="card overflow-x-auto">
            <h2 className="section-title">Most called</h2>
            {u.top.length === 0 ? <p className="text-2xs text-muted">No calls in this period.</p> : (
              <table className="table" data-testid="usage-top">
                <thead><tr><th>Consumer</th><th>Scope</th><th>What</th><th>Calls</th><th>Errors</th><th>429</th><th>Avg</th></tr></thead>
                <tbody>
                  {u.top.map((t) => (
                    <tr key={`${t.consumer}-${t.scope}-${t.target}`}>
                      <td>{u.perConsumer.find((c) => c.name === t.consumer)?.title ?? t.consumer}</td>
                      <td className="text-2xs">{scopeLabel(t.scope)}</td>
                      <td className="mono text-2xs">{t.target}</td>
                      <td>{n(t.calls)}</td><td>{n(t.errors)}</td><td>{n(t.limited)}</td>
                      <td className="text-2xs">{t.avgMs === null ? '—' : formatDuration(t.avgMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <p className="text-2xs text-muted">Counts and times only: no body, no key, no personal data. Days and hours in India time; kept 90 days. An Alert is raised when a consumer reaches 80% of a daily quota and when its calls start to be refused.</p>
        </>
      )}
    </div>
  )
}

function Quota({ used, of }: { used: number; of: number | null }) {
  if (!of) return <span className="text-2xs text-muted">{n(used)} · no quota</span>
  const pct = Math.min(100, Math.round((used / of) * 100))
  const tone = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-primary/80'
  return (
    <div title={`${n(used)} of ${n(of)} today`}>
      <div className="h-1.5 w-full overflow-hidden rounded bg-slate-200 dark:bg-slate-700"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-2xs text-muted">{n(used)} of {n(of)} ({pct}%)</span>
    </div>
  )
}

function UsageChart({ u }: { u: ApiUsage }) {
  const top = Math.max(1, ...u.series.map((p) => p.calls))
  const label = (at: string) => (u.step === 'hour'
    ? new Date(at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })
    : new Date(at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }))
  const every = u.series.length > 12 ? Math.ceil(u.series.length / 8) : 1
  return (
    <div data-testid="usage-chart">
      <div className="flex h-36 items-end gap-1">
        {u.series.map((p) => (
          <div key={p.at} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${label(p.at)}: ${n(p.calls)} call(s), ${n(p.errors)} error(s), ${n(p.limited)} refused`}>
            <div className="flex flex-col justify-end overflow-hidden rounded-t bg-primary/80" style={{ height: `${(p.calls / top) * 100}%`, minHeight: p.calls ? 2 : 0 }}>
              {p.limited > 0 && <div className="bg-amber-500" style={{ height: `${(p.limited / p.calls) * 100}%` }} />}
              {p.errors > 0 && <div className="bg-red-500" style={{ height: `${(p.errors / p.calls) * 100}%` }} />}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1 text-[10px] text-muted">
        {u.series.map((p, i) => <span key={p.at} className="min-w-0 flex-1 truncate text-center">{i % every === 0 ? label(p.at) : ''}</span>)}
      </div>
      <p className="mt-1 text-2xs text-muted"><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-primary/80" />calls <span className="ml-3 mr-1 inline-block h-2 w-2 rounded-sm bg-amber-500" />refused (429) <span className="ml-3 mr-1 inline-block h-2 w-2 rounded-sm bg-red-500" />errors · India time</p>
    </div>
  )
}
